import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, ChevronRight, Plus, Heart, Star, ShoppingBag, Truck, Shield, RefreshCw, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 1: DAWN
 * Style: Clean, Minimalist, Product-focused
 * Best for: Everyday casual wear, basics, essentials
 * Inspired by: Shopify Dawn theme
 */
const Template1 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    const testimonials = [
        {
            text: "The quality is exceptional. These are the most comfortable basics I've ever owned. The fabric feels luxurious against my skin.",
            name: 'Priya S.',
            location: 'Mumbai',
            rating: 5
        },
        {
            text: "Finally found a brand that understands minimalist fashion. Clean designs, perfect fits, and the sustainable approach is a bonus.",
            name: 'Rahul M.',
            location: 'Bangalore',
            rating: 5
        },
        {
            text: "I've replaced most of my wardrobe with their pieces. The attention to detail and fabric quality is absolutely unmatched.",
            name: 'Ananya K.',
            location: 'Delhi',
            rating: 5
        }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial(prev => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [testimonials.length]);

    const handleTestimonialSwipe = (e) => {
        const touchEndX = e.changedTouches[0].clientX;
        const diff = touchStartX.current - touchEndX;
        if (Math.abs(diff) > 50) {
            if (diff > 0) {
                setActiveTestimonial(prev => (prev + 1) % testimonials.length);
            } else {
                setActiveTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
            }
        }
    };

    const categories = [
        { name: 'Women', image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=600&h=800&fit=crop', count: '150+ Styles' },
        { name: 'Men', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=800&fit=crop', count: '120+ Styles' },
        { name: 'Accessories', image: 'https://images.unsplash.com/photo-1606760227091-3dd870d97f1d?w=600&h=800&fit=crop', count: '80+ Items' }
    ];

    const products = [
        { name: 'Oversized Cotton Tee', price: '\u20B91,299', originalPrice: null, badge: 'NEW', image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=500&h=650&fit=crop' },
        { name: 'Relaxed Fit Jeans', price: '\u20B92,499', originalPrice: '\u20B93,299', badge: 'SALE', image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=500&h=650&fit=crop' },
        { name: 'Linen Summer Shirt', price: '\u20B91,899', originalPrice: null, badge: null, image: 'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=500&h=650&fit=crop' },
        { name: 'Classic Crew Sweater', price: '\u20B92,199', originalPrice: null, badge: 'BESTSELLER', image: 'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=500&h=650&fit=crop' },
        { name: 'Tailored Trousers', price: '\u20B92,799', originalPrice: '\u20B93,499', badge: 'SALE', image: 'https://images.unsplash.com/photo-1473966968600-fa801b869a1a?w=500&h=650&fit=crop' },
        { name: 'Minimal Leather Belt', price: '\u20B9999', originalPrice: null, badge: null, image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=500&h=650&fit=crop' },
        { name: 'Canvas Sneakers', price: '\u20B93,499', originalPrice: null, badge: 'TRENDING', image: 'https://images.unsplash.com/photo-1525966222134-fcfa99b8ae77?w=500&h=650&fit=crop' },
        { name: 'Soft Knit Cardigan', price: '\u20B92,599', originalPrice: null, badge: 'NEW', image: 'https://images.unsplash.com/photo-1434510423563-c7e99bbc5bbd?w=500&h=650&fit=crop' }
    ];

    const features = [
        { icon: Truck, title: 'Free Shipping', desc: 'On orders over \u20B91,499' },
        { icon: RefreshCw, title: 'Easy Returns', desc: '30-day return policy' },
        { icon: Shield, title: 'Secure Payment', desc: '100% protected checkout' },
        { icon: Award, title: 'Premium Quality', desc: 'Crafted to last' }
    ];

    const getBadgeColor = (badge) => {
        switch (badge) {
            case 'NEW': return 'bg-emerald-500';
            case 'SALE': return 'bg-red-500';
            case 'BESTSELLER': return 'bg-amber-500';
            case 'TRENDING': return 'bg-blue-500';
            default: return 'bg-gray-500';
        }
    };

    return (
        <div className="min-h-screen bg-white">

            {/* ============================================
                HERO SECTION
            ============================================ */}

            {/* Mobile Hero - stacked layout */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/5]">
                    <img
                        src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1920&q=80"
                        alt="Hero"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="bg-white px-5 py-8">
                    <p className="text-gray-400 text-[11px] tracking-[0.2em] uppercase mb-3">New Collection</p>
                    <h1 className="text-[2rem] leading-[1.1] font-light text-gray-900 mb-4">
                        Effortless Style for Everyday
                    </h1>
                    <p className="text-gray-500 text-[15px] leading-relaxed mb-6 max-w-md">
                        Curated essentials designed with premium materials and timeless silhouettes.
                    </p>
                    <button
                        onClick={() => navigate('/template1/shop')}
                        className="group w-full inline-flex items-center justify-center gap-3 bg-gray-900 text-white px-8 py-4 min-h-[48px] text-[13px] font-medium tracking-widest uppercase hover:bg-gray-800 transition-all active:scale-[0.98]"
                    >
                        Shop Collection
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </section>

            {/* Desktop/Tablet Hero - Full width with overlay text (ORIGINAL) */}
            <section className="hidden lg:block relative w-full h-[85vh] md:h-[90vh] overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1920&q=80"
                        alt="Hero"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/20"></div>
                </div>

                {/* Hero Content */}
                <div className="relative h-full flex items-center">
                    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-xl">
                            <p className="text-white/80 text-sm tracking-[0.2em] uppercase mb-4">
                                New Collection
                            </p>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-6">
                                Effortless Style for Everyday
                            </h1>
                            <p className="text-white/90 text-lg mb-8 font-light">
                                Discover our curated collection of timeless essentials designed for modern living.
                            </p>
                            <button
                                onClick={() => navigate('/template1/shop')}
                                className="group inline-flex items-center gap-3 bg-white text-gray-900 px-8 py-4 text-sm font-medium tracking-wide hover:bg-gray-100 transition-all duration-300"
                            >
                                Shop Collection
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60">
                    <span className="text-xs tracking-widest uppercase">Scroll</span>
                    <div className="w-px h-12 bg-white/40 animate-pulse"></div>
                </div>
            </section>

            {/* ============================================
                FEATURED CATEGORIES
            ============================================ */}

            {/* Mobile Categories - Horizontal scroll */}
            <section className="lg:hidden py-10 sm:py-16">
                <div className="px-5 sm:px-6 mb-6 sm:mb-10">
                    <h2 className="text-xl sm:text-2xl font-light text-gray-900 mb-1.5">
                        Shop by Category
                    </h2>
                    <p className="text-gray-400 text-[14px] sm:text-base">Explore our curated collections</p>
                </div>
                <div className="relative">
                    <div className="flex overflow-x-auto gap-3 px-5 pb-1 snap-x snap-mandatory scrollbar-hide sm:px-6">
                        {categories.map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template1/shop')}
                                className="flex-none w-[68vw] sm:w-[45vw] snap-start group relative aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer"
                            >
                                <img
                                    src={category.image}
                                    alt={category.name}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-5">
                                    <p className="text-white/60 text-[11px] sm:text-xs mb-1">{category.count}</p>
                                    <h3 className="text-white text-lg sm:text-xl font-light mb-2">{category.name}</h3>
                                    <span className="inline-flex items-center gap-1.5 text-white/80 text-[12px] sm:text-sm group-hover:gap-2.5 transition-all">
                                        Shop Now <ChevronRight className="w-3.5 h-3.5" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="absolute top-0 right-0 bottom-1 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
                </div>
            </section>

            {/* Desktop/Tablet Categories - Clean 3-column grid (ORIGINAL) */}
            <section className="hidden lg:block py-16 sm:py-20 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Section Header */}
                    <div className="text-center mb-12 lg:mb-16">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-900 mb-4">
                            Shop by Category
                        </h2>
                        <p className="text-gray-500 max-w-2xl mx-auto">
                            Explore our carefully curated collections designed for every moment
                        </p>
                    </div>

                    {/* Categories Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                        {[
                            {
                                name: 'Women',
                                image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=600&h=800&fit=crop',
                                count: '150+ Styles'
                            },
                            {
                                name: 'Men',
                                image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=800&fit=crop',
                                count: '120+ Styles'
                            },
                            {
                                name: 'Accessories',
                                image: 'https://images.unsplash.com/photo-1606760227091-3dd870d97f1d?w=600&h=800&fit=crop',
                                count: '80+ Items'
                            }
                        ].map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template1/shop')}
                                className="group relative aspect-[3/4] overflow-hidden cursor-pointer"
                            >
                                <img
                                    src={category.image}
                                    alt={category.name}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0"></div>

                                {/* Category Info */}
                                <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                                    <p className="text-white/70 text-sm mb-1">{category.count}</p>
                                    <h3 className="text-white text-xl lg:text-2xl font-light mb-3">{category.name}</h3>
                                    <span className="inline-flex items-center gap-2 text-white text-sm group-hover:gap-3 transition-all">
                                        Shop Now <ChevronRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                FEATURED PRODUCTS
            ============================================ */}

            {/* Mobile Products - Horizontal scroll with wishlist/quick-add */}
            <section className="lg:hidden py-10 sm:py-16 bg-[#fafaf9]">
                <div className="flex items-end justify-between px-5 sm:px-6 mb-6 sm:mb-10">
                    <div>
                        <h2 className="text-xl sm:text-2xl font-light text-gray-900 mb-1">New Arrivals</h2>
                        <p className="text-gray-400 text-[14px] sm:text-base">Fresh styles added weekly</p>
                    </div>
                    <button
                        onClick={() => navigate('/template1/shop')}
                        className="hidden sm:inline-flex items-center gap-2 text-gray-900 text-sm font-medium hover:gap-3 transition-all"
                    >
                        View All <ArrowRight className="w-4 h-4" />
                    </button>
                </div>

                <div className="relative">
                    <div className="flex overflow-x-auto gap-3 px-5 pb-2 snap-x snap-mandatory scrollbar-hide">
                        {products.slice(0, 6).map((product, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template1/shop')}
                                className="flex-none w-[44vw] sm:w-[32vw] snap-start group cursor-pointer"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 rounded-xl mb-2.5">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                    />
                                    {product.badge && (
                                        <div className={`absolute top-2 left-2 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-md text-white ${getBadgeColor(product.badge)}`}>
                                            {product.badge}
                                        </div>
                                    )}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setWishlist(prev => ({ ...prev, [index]: !prev[index] }));
                                        }}
                                        className="absolute top-2 right-2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm active:scale-90 transition-all"
                                    >
                                        <Heart className={`w-3.5 h-3.5 ${wishlist[index] ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
                                    </button>
                                    <button
                                        onClick={(e) => e.stopPropagation()}
                                        className="absolute bottom-2.5 right-2.5 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md active:scale-90 transition-transform"
                                    >
                                        <Plus className="w-4 h-4 text-gray-800" />
                                    </button>
                                </div>
                                <h3 className="text-gray-900 text-[12px] sm:text-sm leading-snug mb-0.5">{product.name}</h3>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-gray-900 text-[12px] sm:text-sm font-medium">{product.price}</span>
                                    {product.originalPrice && (
                                        <span className="text-gray-400 line-through text-[10px] sm:text-xs">{product.originalPrice}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="absolute top-0 right-0 bottom-2 w-8 bg-gradient-to-l from-[#fafaf9] to-transparent pointer-events-none" />
                </div>

                {/* Mobile "View All" button */}
                <div className="mt-8 px-5 sm:hidden">
                    <button
                        onClick={() => navigate('/template1/shop')}
                        className="w-full flex items-center justify-center gap-2 border border-gray-200 text-gray-900 py-3.5 min-h-[48px] text-[13px] font-medium tracking-wide active:bg-gray-50 transition-colors rounded-xl"
                    >
                        View All Products <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </section>

            {/* Desktop/Tablet Products - Clean product grid (ORIGINAL) */}
            <section className="hidden lg:block py-16 sm:py-20 lg:py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Section Header */}
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
                        <div>
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-900 mb-2">
                                New Arrivals
                            </h2>
                            <p className="text-gray-500">Fresh styles added weekly</p>
                        </div>
                        <button
                            onClick={() => navigate('/template1/shop')}
                            className="inline-flex items-center gap-2 text-gray-900 text-sm font-medium hover:gap-3 transition-all"
                        >
                            View All <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Products Grid */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {[
                            { name: 'Oversized Cotton Tee', price: '\u20B91,299', image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=500&h=600&fit=crop' },
                            { name: 'Relaxed Fit Jeans', price: '\u20B92,499', image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=500&h=600&fit=crop' },
                            { name: 'Linen Summer Shirt', price: '\u20B91,899', image: 'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=500&h=600&fit=crop' },
                            { name: 'Classic Crew Sweater', price: '\u20B92,199', image: 'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=500&h=600&fit=crop' },
                            { name: 'Tailored Trousers', price: '\u20B92,799', image: 'https://images.unsplash.com/photo-1473966968600-fa801b869a1a?w=500&h=600&fit=crop' },
                            { name: 'Minimal Leather Belt', price: '\u20B9999', image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=500&h=600&fit=crop' },
                            { name: 'Canvas Sneakers', price: '\u20B93,499', image: 'https://images.unsplash.com/photo-1525966222134-fcfa99b8ae77?w=500&h=600&fit=crop' },
                            { name: 'Soft Knit Cardigan', price: '\u20B92,599', image: 'https://images.unsplash.com/photo-1434510423563-c7e99bbc5bbd?w=500&h=600&fit=crop' }
                        ].map((product, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template1/shop')}
                                className="group cursor-pointer"
                            >
                                {/* Product Image */}
                                <div className="relative aspect-[4/5] overflow-hidden bg-gray-100 mb-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                    {/* Quick Add Button - Shows on hover */}
                                    <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                        <button className="w-full bg-white/95 backdrop-blur-sm text-gray-900 py-3 text-sm font-medium hover:bg-white transition-colors">
                                            Quick Add
                                        </button>
                                    </div>
                                </div>

                                {/* Product Info */}
                                <h3 className="text-gray-900 text-sm lg:text-base mb-1 group-hover:underline underline-offset-4">
                                    {product.name}
                                </h3>
                                <p className="text-gray-600 text-sm">{product.price}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                BRAND STORY
            ============================================ */}

            {/* Mobile Brand Story - Full-bleed stacked */}
            <section className="lg:hidden py-10 sm:py-16">
                <div className="aspect-[4/5] sm:aspect-[3/4] overflow-hidden sm:rounded-2xl sm:mx-6">
                    <img
                        src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=1000&fit=crop"
                        alt="Our Story"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="px-5 sm:px-6 pt-8">
                    <p className="text-gray-400 text-[11px] tracking-[0.2em] uppercase mb-3">Our Story</p>
                    <h2 className="text-xl sm:text-2xl font-light text-gray-900 mb-4 sm:mb-6">
                        Designed for Life, Made for You
                    </h2>
                    <p className="text-gray-500 text-[14px] sm:text-base leading-relaxed mb-4 sm:mb-5">
                        We believe great style should not be complicated. Our collections are thoughtfully designed
                        with premium materials and timeless silhouettes that seamlessly fit into your everyday life.
                    </p>
                    <p className="text-gray-500 text-[14px] sm:text-base leading-relaxed mb-6 sm:mb-8">
                        Each piece is crafted with attention to detail, ensuring comfort and durability that lasts.
                        From sustainable sourcing to ethical manufacturing, we are committed to fashion that feels
                        as good as it looks.
                    </p>
                    <button
                        onClick={() => navigate('/template1/about')}
                        className="inline-flex items-center gap-2 text-gray-900 text-[13px] sm:text-sm font-medium border-b border-gray-900 pb-1 hover:gap-3 transition-all"
                    >
                        Learn More <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </section>

            {/* Desktop/Tablet Brand Story - Split layout with image (ORIGINAL) */}
            <section className="hidden lg:block py-16 sm:py-20 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        {/* Image */}
                        <div className="relative aspect-[4/5] lg:aspect-square overflow-hidden">
                            <img
                                src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=800&fit=crop"
                                alt="Our Story"
                                className="w-full h-full object-cover"
                            />
                        </div>

                        {/* Content */}
                        <div className="lg:pl-8">
                            <p className="text-gray-500 text-sm tracking-[0.2em] uppercase mb-4">Our Story</p>
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-900 mb-6 leading-tight">
                                Designed for Life, Made for You
                            </h2>
                            <p className="text-gray-600 text-lg leading-relaxed mb-6">
                                We believe great style shouldn't be complicated. Our collections are thoughtfully designed
                                with premium materials and timeless silhouettes that seamlessly fit into your everyday life.
                            </p>
                            <p className="text-gray-600 leading-relaxed mb-8">
                                Each piece is crafted with attention to detail, ensuring comfort and durability that lasts.
                                From sustainable sourcing to ethical manufacturing, we're committed to fashion that feels
                                as good as it looks.
                            </p>
                            <button
                                onClick={() => navigate('/template1/about')}
                                className="inline-flex items-center gap-2 text-gray-900 text-sm font-medium border-b border-gray-900 pb-1 hover:gap-3 transition-all"
                            >
                                Learn More <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============================================
                FEATURES / TRUST BADGES (Mobile only - not in original)
            ============================================ */}
            <section className="lg:hidden py-8 sm:py-12 bg-[#fafaf9] border-y border-gray-100">
                <div className="flex overflow-x-auto gap-5 px-5 snap-x snap-mandatory scrollbar-hide">
                    {features.map((feature, index) => (
                        <div key={index} className="flex-none w-[55vw] sm:w-[35vw] snap-start flex items-center gap-3 py-2">
                            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <feature.icon className="w-5 h-5 text-gray-600" />
                            </div>
                            <div>
                                <h3 className="text-gray-900 text-[13px] font-medium">{feature.title}</h3>
                                <p className="text-gray-400 text-[11px]">{feature.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ============================================
                LOOKBOOK - Visual storytelling grid (Desktop/Tablet only - ORIGINAL)
            ============================================ */}
            <section className="hidden lg:block py-16 sm:py-20 lg:py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Section Header */}
                    <div className="text-center mb-12 lg:mb-16">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-900 mb-4">
                            The Lookbook
                        </h2>
                        <p className="text-gray-500 max-w-2xl mx-auto">
                            Get inspired by our latest editorial featuring this season's must-have pieces
                        </p>
                    </div>

                    {/* Lookbook Grid - Masonry style */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Large Image */}
                        <div className="col-span-2 row-span-2 relative aspect-square overflow-hidden group cursor-pointer">
                            <img
                                src="https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=800&h=800&fit=crop"
                                alt="Lookbook 1"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"></div>
                            <div className="absolute bottom-6 left-6 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                <p className="text-sm font-medium">Shop the Look</p>
                            </div>
                        </div>

                        {/* Small Images */}
                        {[
                            'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=500&h=500&fit=crop',
                            'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=500&h=500&fit=crop',
                            'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=500&h=500&fit=crop',
                            'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=500&h=500&fit=crop'
                        ].map((image, index) => (
                            <div key={index} className="relative aspect-square overflow-hidden group cursor-pointer">
                                <img
                                    src={image}
                                    alt={`Lookbook ${index + 2}`}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                TESTIMONIALS
            ============================================ */}

            {/* Mobile Testimonials - Swipeable carousel */}
            <section className="lg:hidden py-10 sm:py-16">
                <div className="px-5 sm:px-6">
                    <div className="text-center mb-8 sm:mb-12">
                        <h2 className="text-xl sm:text-2xl font-light text-gray-900 mb-1.5">
                            What Our Customers Say
                        </h2>
                        <p className="text-gray-400 text-[14px] sm:text-base">Real reviews from real people</p>
                    </div>

                    <div
                        className="relative overflow-hidden"
                        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                        onTouchEnd={handleTestimonialSwipe}
                    >
                        <div
                            className="flex transition-transform duration-400 ease-out"
                            style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
                        >
                            {testimonials.map((testimonial, index) => (
                                <div key={index} className="w-full flex-none px-2">
                                    <div className="bg-[#fafaf9] rounded-2xl p-6 sm:p-8 text-center">
                                        <div className="flex justify-center gap-1 mb-4">
                                            {[...Array(testimonial.rating)].map((_, i) => (
                                                <Star key={i} className="w-4 h-4 fill-gray-900 text-gray-900" />
                                            ))}
                                        </div>
                                        <p className="text-gray-600 text-[14px] leading-relaxed mb-5 italic">
                                            "{testimonial.text}"
                                        </p>
                                        <p className="text-gray-900 font-medium text-sm">{testimonial.name}</p>
                                        <p className="text-gray-400 text-[11px] mt-0.5">{testimonial.location}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-center gap-2 mt-5">
                        {testimonials.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setActiveTestimonial(index)}
                                className={`h-1.5 rounded-full transition-all duration-300 ${
                                    index === activeTestimonial ? 'w-6 bg-gray-900' : 'w-1.5 bg-gray-300'
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop/Tablet Testimonials - Grid (ORIGINAL) */}
            <section className="hidden lg:block py-16 sm:py-20 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Section Header */}
                    <div className="text-center mb-12 lg:mb-16">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-900 mb-4">
                            What Our Customers Say
                        </h2>
                    </div>

                    {/* Testimonials Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                text: "The quality is exceptional. These are the most comfortable basics I've ever owned. Will definitely be ordering more!",
                                name: "Priya S.",
                                location: "Mumbai"
                            },
                            {
                                text: "Finally found a brand that understands minimalist fashion. Clean designs, perfect fits, and sustainable too.",
                                name: "Rahul M.",
                                location: "Bangalore"
                            },
                            {
                                text: "I've replaced most of my wardrobe with their pieces. The attention to detail and fabric quality is unmatched.",
                                name: "Ananya K.",
                                location: "Delhi"
                            }
                        ].map((testimonial, index) => (
                            <div key={index} className="text-center">
                                {/* Stars */}
                                <div className="flex justify-center gap-1 mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <svg key={i} className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                    ))}
                                </div>
                                <p className="text-gray-600 italic mb-6 leading-relaxed">"{testimonial.text}"</p>
                                <p className="text-gray-900 font-medium">{testimonial.name}</p>
                                <p className="text-gray-500 text-sm">{testimonial.location}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                NEWSLETTER
            ============================================ */}

            {/* Mobile Newsletter - Stacked rounded */}
            <section className="lg:hidden py-12 sm:py-16 bg-gray-900 text-white">
                <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
                    <h2 className="text-xl sm:text-2xl font-light mb-3">
                        Join the Community
                    </h2>
                    <p className="text-gray-400 text-[14px] sm:text-base mb-7 sm:mb-8 max-w-md mx-auto">
                        Subscribe for early access to new arrivals, exclusive offers, and style inspiration.
                    </p>
                    <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="w-full flex-1 px-5 py-3.5 bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:border-white/40 transition-colors rounded-xl sm:rounded-none text-[14px]"
                        />
                        <button
                            type="submit"
                            className="w-full sm:w-auto px-8 py-3.5 min-h-[48px] bg-white text-gray-900 font-medium hover:bg-gray-100 transition-colors rounded-xl sm:rounded-none text-[14px]"
                        >
                            Subscribe
                        </button>
                    </form>
                    <p className="text-gray-600 text-[11px] sm:text-xs mt-4">
                        No spam, unsubscribe anytime.
                    </p>
                </div>
            </section>

            {/* Desktop/Tablet Newsletter - Simple signup section (ORIGINAL) */}
            <section className="hidden lg:block py-16 sm:py-20 lg:py-24 bg-gray-900 text-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light mb-4">
                        Join the Community
                    </h2>
                    <p className="text-gray-400 mb-8">
                        Subscribe for early access to new arrivals, exclusive offers, and style inspiration.
                    </p>
                    <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-white/40 transition-colors"
                        />
                        <button
                            type="submit"
                            className="px-8 py-3 bg-white text-gray-900 font-medium hover:bg-gray-100 transition-colors"
                        >
                            Subscribe
                        </button>
                    </form>
                    <p className="text-gray-500 text-sm mt-4">
                        No spam, unsubscribe anytime.
                    </p>
                </div>
            </section>

            {/* ============================================
                INSTAGRAM FEED - Shared responsive grid
            ============================================ */}
            <section className="py-10 sm:py-16 lg:py-20">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-5 sm:mb-8">
                        <p className="text-gray-400 text-[11px] sm:text-sm tracking-[0.2em] uppercase">@yourbrandname</p>
                    </div>

                    {/* Instagram Grid */}
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-0.5 sm:gap-1 lg:gap-2 px-0 sm:px-6 lg:px-8">
                        {[
                            'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=300&h=300&fit=crop',
                            'https://images.unsplash.com/photo-1445205170230-053b83016050?w=300&h=300&fit=crop',
                            'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=300&h=300&fit=crop',
                            'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=300&h=300&fit=crop',
                            'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=300&h=300&fit=crop',
                            'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=300&h=300&fit=crop'
                        ].map((image, index) => (
                            <div key={index} className="relative aspect-square overflow-hidden group cursor-pointer">
                                <img
                                    src={image}
                                    alt={`Instagram ${index + 1}`}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                    <svg className="w-5 h-5 lg:w-6 lg:h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                    </svg>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

        </div>
    );
};

export default Template1;
